import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { cleanBlock, plainTitle } from '@/lib/noteUtils';

interface NoteContentProps {
  event: NostrEvent;
  /**
   * The record's title (from getNoteHeading), shown in bold exactly as on its
   * card. When the note opens with its title, that opening paragraph is made
   * bold in place; when the title comes from a tag and isn't in the text, it is
   * added above. Null or omitted leaves the text as written.
   */
  title?: string | null;
}

/** The first paragraph of the text, split from the rest (the rest keeps its leading blank line). */
function splitFirstBlock(content: string): { head: string; tail: string } {
  const m = /\n\s*\n/.exec(content);
  if (!m) return { head: content, tail: '' };
  return { head: content.slice(0, m.index), tail: content.slice(m.index) };
}

/**
 * Render note content with linkified URLs, hashtags, and nostr: mentions.
 * Uses a safe approach — no dangerouslySetInnerHTML.
 */
export function NoteContent({ event, title }: NoteContentProps) {
  const { head, tail } = splitFirstBlock(event.content);
  const openingIsTitle =
    Boolean(title) && plainTitle(cleanBlock(head)).toLowerCase() === title!.toLowerCase();

  // No title, or an ordinary note: the text exactly as written.
  if (!title) {
    return (
      <div className={bodyClass}>
        <Segments segments={parseContent(event.content, event)} />
      </div>
    );
  }

  return (
    <div className={bodyClass}>
      {openingIsTitle ? (
        <>
          <span className="font-bold leading-snug text-xl md:text-2xl">
            <Segments segments={parseContent(head, event)} />
          </span>
          <Segments segments={parseContent(tail, event)} />
        </>
      ) : (
        <>
          <span className="mb-4 block font-bold leading-snug text-xl md:text-2xl">{title}</span>
          <Segments segments={parseContent(event.content, event)} />
        </>
      )}
    </div>
  );
}

const bodyClass =
  'text-base md:text-lg leading-relaxed whitespace-pre-wrap break-words font-serif text-foreground';

function Segments({ segments }: { segments: Segment[] }) {
  return (
    <>
      {segments.map((seg, i) => {
        switch (seg.type) {
          case 'text':
            return <span key={i}>{seg.value}</span>;

          case 'image':
            return (
              <span key={i} className="block my-4">
                <img
                  src={seg.value}
                  alt="Image attached to this record"
                  className="max-w-full rounded-lg border border-border bg-muted shadow-sm"
                  loading="lazy"
                  decoding="async"
                />
              </span>
            );

          case 'video':
            return (
              <span key={i} className="block my-4">
                <video
                  src={seg.value}
                  controls
                  playsInline
                  preload="metadata"
                  className="max-w-full w-full rounded-lg border border-border bg-black"
                />
              </span>
            );

          case 'unknown-media':
            // URL has no recognisable extension — try video first, fall back to image, then link
            return <SmartMedia key={i} url={seg.value} />;

          case 'url':
            return (
              <a
                key={i}
                href={seg.value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 break-all hover:opacity-70 transition-opacity"
              >
                {seg.value}
              </a>
            );

          case 'hashtag':
            return (
              <span key={i} className="text-primary font-medium cursor-default">
                #{seg.value}
              </span>
            );

          case 'nostr': {
            const { raw, decoded } = seg;
            try {
              if (decoded.type === 'npub' || decoded.type === 'nprofile') {
                const npub =
                  decoded.type === 'npub'
                    ? nip19.npubEncode(decoded.data as string)
                    : nip19.nprofileEncode(decoded.data as { pubkey: string; relays?: string[] });
                return (
                  <Link
                    key={i}
                    to={`/${npub}`}
                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity font-mono text-sm"
                  >
                    @{npub.slice(0, 12)}…
                  </Link>
                );
              }
              if (decoded.type === 'note' || decoded.type === 'nevent') {
                return (
                  <Link
                    key={i}
                    to={`/${raw}`}
                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity font-mono text-sm"
                  >
                    note:{raw.slice(0, 12)}…
                  </Link>
                );
              }
            } catch {
              // fall through
            }
            return (
              <span key={i} className="font-mono text-sm text-muted-foreground">
                nostr:{raw.slice(0, 16)}…
              </span>
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// SmartMedia — tries video first (most Nostr CDN links are video), then image
// ---------------------------------------------------------------------------

function SmartMedia({ url }: { url: string }) {
  // 'video' | 'image' | 'link'
  const [mode, setMode] = useState<'video' | 'image' | 'link'>('video');

  if (mode === 'video') {
    return (
      <span className="block my-4">
        <video
          src={url}
          controls
          playsInline
          preload="metadata"
          className="max-w-full w-full rounded-lg border border-border bg-black"
          onError={() => setMode('image')}
        />
      </span>
    );
  }

  if (mode === 'image') {
    return (
      <span className="block my-4">
        <img
          src={url}
          alt="Image attached to this record"
          className="max-w-full rounded-lg border border-border bg-muted shadow-sm"
          loading="lazy"
          decoding="async"
          onError={() => setMode('link')}
        />
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 break-all hover:opacity-70 transition-opacity"
    >
      {url}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Segment =
  | { type: 'text'; value: string }
  | { type: 'image'; value: string }
  | { type: 'video'; value: string }
  | { type: 'unknown-media'; value: string }
  | { type: 'url'; value: string }
  | { type: 'hashtag'; value: string }
  | { type: 'nostr'; raw: string; decoded: ReturnType<typeof nip19.decode> };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip trailing punctuation that often gets captured as part of URLs */
function cleanUrl(raw: string): string {
  return raw.replace(/[.,!?;:'")\]>]+$/, '');
}

/** Returns true if the URL has an image file extension */
function isImageUrl(url: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp|avif|svg)(\?[^#]*)?$/i.test(url);
}

/** Returns true if the URL has a video file extension */
function isVideoExtension(url: string): boolean {
  return /\.(mp4|webm|ogg|mov|m4v|mkv)(\?[^#]*)?$/i.test(url);
}

/**
 * Build the set of all URLs referenced by imeta tags keyed by mime type prefix.
 * Returns a Map<url, mimeType>.
 */
function buildImetaMap(event: NostrEvent): Map<string, string> {
  const map = new Map<string, string>();
  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    const vals = tag.slice(1);
    let tagUrl: string | undefined;
    let tagMime: string | undefined;
    for (const v of vals) {
      if (v.startsWith('url ')) tagUrl = v.slice(4).trim();
      if (v.startsWith('m ')) tagMime = v.slice(2).trim();
    }
    if (tagUrl && tagMime) map.set(tagUrl, tagMime);
  }
  return map;
}

/**
 * Returns true if the URL is from a known Nostr media CDN
 * that commonly serves extensionless media files.
 */
function isMediaCdnUrl(url: string): boolean {
  try {
    const { hostname, pathname } = new URL(url);
    const mediaCdns = [
      'm.primal.net',
      'primal.b-cdn.net',
      'void.cat',
      'nostr.build',
      'cdn.nostr.build',
      'blossom.band',
      'blossom.primal.net',
      'blossom.ditto.pub',
      'blossom.oxtr.dev',
      'satellite.earth',
      'cdn.satellite.earth',
      'media.nostr.band',
      'files.v0l.io',
      'nostrcheck.me',
    ];
    if (mediaCdns.some(cdn => hostname === cdn || hostname.endsWith('.' + cdn))) return true;
    // Paths that look like content-addressed blobs (long hex or base64-ish segments)
    if (/\/[0-9a-f]{40,}/i.test(pathname)) return true;
    return false;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

function parseContent(content: string, event: NostrEvent): Segment[] {
  const imetaMap = buildImetaMap(event);
  const segments: Segment[] = [];
  const pattern = /(https?:\/\/[^\s]+)|(#[a-zA-Z0-9_]+)|(nostr:[a-z0-9]+)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }

    if (match[1]) {
      const url = cleanUrl(match[1]);
      const stripped = match[1].slice(url.length);

      if (url.startsWith('https://')) {
        const mime = imetaMap.get(url);

        if (mime) {
          // imeta tag tells us the exact type
          if (mime.startsWith('video/')) {
            segments.push({ type: 'video', value: url });
          } else if (mime.startsWith('image/')) {
            segments.push({ type: 'image', value: url });
          } else {
            segments.push({ type: 'url', value: url });
          }
        } else if (isImageUrl(url)) {
          segments.push({ type: 'image', value: url });
        } else if (isVideoExtension(url)) {
          segments.push({ type: 'video', value: url });
        } else if (isMediaCdnUrl(url)) {
          // CDN URL with no extension — let SmartMedia probe it
          segments.push({ type: 'unknown-media', value: url });
        } else {
          segments.push({ type: 'url', value: url });
        }
      } else {
        segments.push({ type: 'text', value: url });
      }

      if (stripped) segments.push({ type: 'text', value: stripped });
    } else if (match[2]) {
      segments.push({ type: 'hashtag', value: match[2].slice(1) });
    } else if (match[3]) {
      const raw = match[3].slice('nostr:'.length);
      try {
        const decoded = nip19.decode(raw);
        segments.push({ type: 'nostr', raw, decoded });
      } catch {
        segments.push({ type: 'text', value: match[3] });
      }
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }

  return segments;
}
